import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import ModalGenericPostCancelContainer from "../../../Common/ModalGenericPostCancelContainer";
import EndreIleggelseModalForm from "./EndreIleggelseModalForm";

import {
  putBillett,
  putKjoretoy,
  putStedsbeskrivelse,
} from "../../../../actions/commonActions";
import {
  getIleggelse as getIleggelseAction,
  getIleggelseHistorikk as getIleggelseHistorikkAction,
  putIleggelser as putIleggelserAction,
} from "../../../../actions/ileggelserActions";

import {
  getIleggelseEttersendelser as getIleggelseEttersendelserAction,
  postIleggelseEttersendelser as postIleggelseEttersendelserAction,
} from "../../../../actions/ileggelseEttersendelserActions";
import { getIleggelseleveringstyper as getIleggelseleveringstyperAction } from "../../../../actions/ileggelseleveringstyperActions";
import { getInternkoderIleggelser as getInternkoderIleggelserAction } from "../../../../actions/internkoderIleggelserActions";
import {
  GET_ILEGGELSE_SINGLE_MOTORVOGN_SUCCESS,
  getSingleMotorvognByQuery as getSingleMotorvognByQueryAction,
} from "../../../../actions/motorvognActions";
import { getOvertredelserkoderIleggelser as getOvertredelserkoderIleggelserAction } from "../../../../actions/overtredelseskoderIleggelserActions";

import { getOvertredelseskoderIleggelseAsDropDownListSearchFriendly } from "../../../../selectors/ileggelseSelector";
import { getSelectOptionsEnhanced } from "../../../../selectors/selectOptionsSelector";

import { dateTimeFromTimePicker } from "../../../../utils/date";
import { stateModified } from "../../../../utils/stateUtils";

import { commentHasToBeBetween } from "../../../../constants/ErrorMessages";
import {
  ettersendelsesrapportMaxLength,
  ettersendelsesrapportMinLength,
} from "../../../../constants/globals";

const buildInitialState = (ileggelse) => ({
  leveringstype: ileggelse.leveringstype ? ileggelse.leveringstype.id : null,
  overtredelse1: ileggelse.overtredelse1 ? ileggelse.overtredelse1.id : null,
  overtredelse2: ileggelse.overtredelse2 ? ileggelse.overtredelse2.id : null,
  overtredelse3: ileggelse.overtredelse3 ? ileggelse.overtredelse3.id : null,
  internkode1: ileggelse.internkode1 ? ileggelse.internkode1.id : null,
  internkode2: ileggelse.internkode2 ? ileggelse.internkode2.id : null,
  ettersendelsesrapport: null,
  showEttersendelsesrapportTextArea: false,
  ettersendelsesrapportValidationErrorText: null,
  kjennemerke: (ileggelse.kjoretoy && ileggelse.kjoretoy.kjennemerke) || "",
  kjoretoyType: (ileggelse.kjoretoy && ileggelse.kjoretoy.kjoretoyType) || "",
  fabrikkmerke: (ileggelse.kjoretoy && ileggelse.kjoretoy.fabrikkmerke) || "",
  annetFabrikkMerke:
    (ileggelse.kjoretoy && ileggelse.kjoretoy.modellType) || "-",
  gateNavn: ileggelse.gatenavn || "",
  gateNummer: ileggelse.gatenummer || "",
  oppgang: ileggelse.oppgang || "",
  bydel: ileggelse.bydel || "",
  vedGateNummer: ileggelse.vedGatenummer || "",
  vedGate: ileggelse.vedGate || "",
  antallMeter: ileggelse.antallMeter || "",
  belop: ileggelse.belop || "",
  skiltFraTid: ileggelse.skiltFraTid,
  skiltTilTid: ileggelse.skiltTilTid,
  billettNummer: ileggelse.billettNummer || "",
  internkommentar: ileggelse.internkommentar || "",
  kommentarTilMottaker: ileggelse.kommentar || "",
  kjoretoy: {},
  stedModified: false,
  ileggelseModified: false,
  ettersendelsesrapportModified: false,
  motorvognModified: false,
  billettModified: false,
  betaltKode: ileggelse && ileggelse.betaltKode,
  betaltDato: ileggelse && ileggelse.betaltDato,
  sendtTilKemnerDato: ileggelse && ileggelse.sendtTilKemnerDato,
});

const rules = { belop: "required;isDigit" };

const ileggelseProps = () => [
  "skiltFraTid",
  "skiltTilTid",
  "antallMeter",
  "leveringstype",
  "overtredelse1",
  "overtredelse2",
  "overtredelse3",
  "internkode1",
  "internkode2",
  "internkode3",
  "kommentarTilMottaker",
  "internkommentar",
  "belop",
];

const stedsBeskrivelseProps = () => [
  "bydel",
  "gateNavn",
  "gateNummer",
  "oppgang",
  "vedGate",
  "vedGateNummer",
];

export default function EndreIleggelseContainer() {
  const ileggelse = useSelector((state) => state.ileggelse);
  const leveringstyper = useSelector((state) =>
    getSelectOptionsEnhanced(state, "ileggelseleveringstyper", "navn", "id"),
  );
  const overtredelseskoder = useSelector((state) =>
    getOvertredelseskoderIleggelseAsDropDownListSearchFriendly(state),
  );
  const internkoder = useSelector((state) =>
    getSelectOptionsEnhanced(
      state,
      "internkoderIleggelser",
      "beskrivelse",
      "id",
    ),
  );
  const dispatch = useDispatch();

  const [formState, setFormState] = useState(() =>
    buildInitialState(ileggelse),
  );
  // Track the initial state separately so checkStateIsModified can compare
  const [initialState] = useState(() => buildInitialState(ileggelse));

  useEffect(() => {
    dispatch(getIleggelseleveringstyperAction());
    dispatch(getOvertredelserkoderIleggelserAction());
    dispatch(getInternkoderIleggelserAction());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror componentDidUpdate logic: show/hide ettersendelsesrapport textarea
  useEffect(() => {
    const { showEttersendelsesrapportTextArea, leveringstype } = formState;
    if (
      !showEttersendelsesrapportTextArea &&
      leveringstype === 3 &&
      ileggelse &&
      ileggelse.leveringstype &&
      ileggelse.leveringstype.id !== leveringstype
    ) {
      setFormState((s) => ({ ...s, showEttersendelsesrapportTextArea: true }));
    } else if (
      showEttersendelsesrapportTextArea &&
      ileggelse &&
      ileggelse.leveringstype &&
      ileggelse.leveringstype.id === leveringstype
    ) {
      setFormState((s) => ({
        ...s,
        showEttersendelsesrapportTextArea: false,
        ettersendelsesrapport: null,
        ettersendelsesrapportValidationErrorText: null,
        ettersendelsesrapportModified: false,
      }));
    }
  }, [formState.leveringstype]); // eslint-disable-line react-hooks/exhaustive-deps

  const areInputFieldsValidChange = (name, value) => {
    if (
      name === "ettersendelsesrapport" &&
      formState.ettersendelsesrapportValidationErrorText &&
      value &&
      value.length >= ettersendelsesrapportMinLength &&
      value.length <= ettersendelsesrapportMaxLength
    ) {
      setFormState((s) => ({
        ...s,
        ettersendelsesrapportValidationErrorText: null,
      }));
    }
  };

  const checkStateIsModified = (name, val) => {
    const snapshot = { ...formState };
    if (includes(stedsBeskrivelseProps(), name)) {
      stateModified(name, val, initialState, snapshot, "stedModified");
      setFormState((prev) => ({ ...prev, stedModified: snapshot.stedModified }));
    } else if (includes(ileggelseProps(), name)) {
      stateModified(name, val, initialState, snapshot, "ileggelseModified");
      setFormState((prev) => ({
        ...prev,
        ileggelseModified: snapshot.ileggelseModified,
      }));
    } else if (
      name === "ettersendelsesrapport" &&
      initialState &&
      val !== initialState.ettersendelsesrapport
    ) {
      stateModified(name, val, initialState, snapshot, "ettersendelsesrapportModified");
      setFormState((prev) => ({
        ...prev,
        ettersendelsesrapportModified: snapshot.ettersendelsesrapportModified,
      }));
    } else if (name === "billettNummer" && val && val !== "") {
      stateModified(name, val, initialState, snapshot, "billettModified");
      setFormState((prev) => ({
        ...prev,
        billettModified: snapshot.billettModified,
      }));
    }
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setFormState((s) => ({ ...s, [name]: value }));
    areInputFieldsValidChange(name, value);
    checkStateIsModified(name, value);
  };

  const generateKjoretoyPayload = (motorvogn) => {
    if (!motorvogn && !motorvogn.kjennemerke) return null;
    const payload = Object.assign({
      kjennemerke: motorvogn.kjennemerke,
      kjoretoyGruppe: motorvogn.typeBeskrivelse,
      modell: motorvogn.modell,
      fabrikkmerke: motorvogn.merke,
      modellType: motorvogn.typeVerdi,
      chassisNummer: motorvogn.understellsnummer,
      fargekode: motorvogn.fargeKode,
      farge: motorvogn.farge,
      totalvekt: motorvogn.tillattTotalvekt,
      euronorm: motorvogn.euroklasse,
      kjoretoyType: motorvogn.typeNavn,
      nasjon: "Norge",
      nasjonKode: "N",
    });
    setFormState((s) => ({
      ...s,
      kjoretoy: payload,
      kjoretoyType: payload.kjoretoyType,
      fabrikkmerke: payload.fabrikkmerke,
      annetFabrikkMerke: payload.modellType,
    }));
    return payload;
  };

  const hentFraMotorvogn = () => {
    const { kjennemerke } = formState;
    if (!kjennemerke || kjennemerke === "") return null;
    dispatch(
      getSingleMotorvognByQueryAction(
        { kjennemerke },
        GET_ILEGGELSE_SINGLE_MOTORVOGN_SUCCESS,
      ),
    ).then((response) => {
      if (response.data.result.kjennemerke) {
        generateKjoretoyPayload(response.data.result);
        setFormState((s) => ({ ...s, motorvognModified: true }));
      }
    });
  };

  const areInputFieldsValidSubmit = () => {
    const { showEttersendelsesrapportTextArea, ettersendelsesrapport } =
      formState;
    if (
      showEttersendelsesrapportTextArea &&
      (!ettersendelsesrapport ||
        ettersendelsesrapport.length < ettersendelsesrapportMinLength ||
        ettersendelsesrapport.length > ettersendelsesrapportMaxLength)
    ) {
      const errText = commentHasToBeBetween(
        ettersendelsesrapportMinLength,
        ettersendelsesrapportMaxLength,
      );
      setFormState((s) => ({
        ...s,
        ettersendelsesrapportValidationErrorText: errText,
      }));
      return false;
    }
    return true;
  };

  const handleApiCall = async (ileggelseId) => {
    if (!areInputFieldsValidSubmit()) {
      return Promise.reject("Valideringsfeil");
    }
    const { stedsBeskrivelseId, billettId, kjoretoy } = ileggelse;
    const {
      stedModified,
      ileggelseModified,
      ettersendelsesrapportModified,
      motorvognModified,
      billettModified,
    } = formState;
    let skiltFraTid = formState.skiltFraTid
      ? dateTimeFromTimePicker(formState.skiltFraTid)
      : null;
    let skiltTilTid = formState.skiltTilTid
      ? dateTimeFromTimePicker(formState.skiltTilTid)
      : null;
    let putError = null;

    if (ileggelseModified && !putError) {
      const putIleggelserData = await dispatch(
        putIleggelserAction(ileggelseId, {
          leveringstype: formState.leveringstype,
          overtredelse1: formState.overtredelse1,
          overtredelse2: formState.overtredelse2,
          overtredelse3: formState.overtredelse3,
          internkode1: formState.internkode1,
          internkode2: formState.internkode2,
          antallMeter: formState.antallMeter,
          belop: formState.belop && Number(formState.belop),
          internkommentar: formState.internkommentar,
          kommentarTilMottaker: formState.kommentarTilMottaker,
          skiltFraTid,
          skiltTilTid,
        }),
      );
      putError = putIleggelserData.resolved ? putError : putIleggelserData.data;
    }
    if (ettersendelsesrapportModified && !putError) {
      await dispatch(
        postIleggelseEttersendelserAction(ileggelse.id, {
          kommentar: formState.ettersendelsesrapport,
        }),
      )
        .then(() => {
          putError = null;
          dispatch(getIleggelseEttersendelserAction(ileggelse.id));
        })
        .catch((error) => {
          putError = error;
        });
    }
    if (motorvognModified && !putError) {
      const kjoretoyState = formState.kjoretoy;
      const kjoretoyId = kjoretoy && kjoretoy.id;
      if (!isEmpty(kjoretoyState)) {
        const putKjoretoyData = await dispatch(
          putKjoretoy(kjoretoyId, {
            ...kjoretoyState,
            totalvekt:
              kjoretoyState.totalvekt && kjoretoyState.totalvekt.toString(),
          }),
        );
        putError = putKjoretoyData.resolved ? putError : putKjoretoyData.data;
      }
    }
    if (stedModified && !putError) {
      const putStedsbeskrivelseData = await dispatch(
        putStedsbeskrivelse(stedsBeskrivelseId, formState),
      );
      putError = putStedsbeskrivelseData.resolved
        ? putError
        : putStedsbeskrivelseData.data;
    }
    if (billettModified && !putError) {
      const putBillettData = await dispatch(
        putBillett(billettId, formState.billettNummer),
      );
      putError = putBillettData.resolved ? putError : putBillettData.data;
    }
    if (
      !putError &&
      (ileggelseModified ||
        ettersendelsesrapportModified ||
        motorvognModified ||
        stedModified ||
        billettModified)
    ) {
      dispatch(getIleggelseAction(ileggelseId));
      await dispatch(getIleggelseHistorikkAction(ileggelseId));
      setFormState((s) => ({
        ...s,
        stedModified: false,
        ileggelseModified: false,
        ettersendelsesrapportModified: false,
        motorvognModified: false,
        billettModified: false,
      }));
      return Promise.resolve(true);
    }
    if (putError) return Promise.reject(putError);
    return false;
  };

  const showEndreIleggelseModalForm = () =>
    formState.leveringstype &&
    formState.overtredelse1 &&
    leveringstyper &&
    overtredelseskoder &&
    internkoder;

  if (
    !ileggelse.isMakulert &&
    ileggelse.forsinketMakuleringStatus &&
    !ileggelse.forsinketMakuleringStatus.deletePossible
  ) {
    return (
      <ModalGenericPostCancelContainer
        className="ileggelseDetailPutModal"
        id={ileggelse.id}
        modalTitle={`Endre ileggelse ${ileggelse.ileggelsesnummer}`}
        submitButtonText="Lagre endring"
        submittingButtonText="Lagrer endring..."
        successPostCallFlashMessage="Endring fullført"
        errorPostCallFlashMessage="Endring feilet"
        handleApiCall={handleApiCall}
        closeModal={() => setFormState(buildInitialState(ileggelse))}
        openModalComponentFunction={(props) => (
          <Button
            className="btn btn-bym-standard"
            style={{ marginLeft: 20 }}
            onClick={props.openModal}
          >
            Endre ileggelse
          </Button>
        )}
      >
        {" "}
        {showEndreIleggelseModalForm() && (
          <EndreIleggelseModalForm
            {...formState}
            warningText={
              ileggelse.isSendtKemner
                ? "Denne ileggelsen er allerede sendt til INE"
                : ""
            }
            showEttersendelsesrapportTextArea={
              formState.showEttersendelsesrapportTextArea
            }
            ileggelse={ileggelse}
            ileggelseleveringstyper={leveringstyper}
            overtredelseskoder={overtredelseskoder}
            internkoder={internkoder}
            handleChange={handleChange}
            hentFraMotorvogn={hentFraMotorvogn}
          />
        )}
      </ModalGenericPostCancelContainer>
    );
  }
  return <span />;
}
